import type { ReactNode } from "react";
import { Tooltip as RadixTooltip } from "radix-ui";

export interface TooltipProps {
  /** What the thing does, in a word or three. */
  label: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  /** The one element the tip belongs to. It must take a ref and props. */
  children: ReactNode;
}

/**
 * The name of a control that is only an icon.
 *
 * A small dark label. It replaced the
 * browser's `title`, which arrives a second late in the system's own grey
 * box, never on a keyboard and never on a phone; this one shows on focus as
 * well as on hover. It carries its own provider so a tip works wherever the
 * control is put, including in a test that mounts one button.
 */
export function Tooltip({ label, side = "bottom", children }: TooltipProps) {
  return (
    <RadixTooltip.Provider delayDuration={350} skipDelayDuration={200}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className="z-[70] max-w-64 select-none rounded-xs bg-tip px-2 py-1 text-2xs font-medium text-tip-fg shadow-raised"
          >
            {label}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
